import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { trackCTAClick, trackThoughtSelect } from '../utils/analytics';

const thoughts = [
  "I don't know what to do",
  'I feel lost',
  'I keep second-guessing everything',
  'Why do I keep feeling this way?',
  "I'm not sure I'm making the right choices",
  "I need to figure this out",
    'I need career guidance',
    'Something else',
];

export default function FeelingPage() {
  const navigate = useNavigate();
  const [selectedThoughts, setSelectedThoughts] = useState([]);

  const toggleThought = (thought) => {
    trackThoughtSelect(thought, '/feeling');
    setSelectedThoughts((prev) => {
      if (prev.includes(thought)) {
        return prev.filter((item) => item !== thought);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, thought];
    });
  };

  return (
    <>
      <Helmet>
        <title>Feeling Stuck, Lost, Or Unsure? | Andlegur</title>
        <meta
          name="description"
          content="When everything feels unclear, Andlegur gives you a private place to understand what is going on and find direction."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://andlegur.com/feeling" />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#05091B] font-serif text-indigo-100">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img
            src="/web_images/Andlegur%20web%2014.webp"
            alt=""
            className="h-full w-full object-cover opacity-18 mix-blend-screen"
            loading="eager"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/35 via-transparent to-[#05091B]" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/60" />
        </div>

        <div className="relative z-10 min-h-screen px-4 sm:px-8 py-8 sm:py-10 flex items-center justify-center">
          <section className="w-full max-w-4xl rounded-3xl border border-indigo-400/50 bg-[#05091B] p-5 sm:p-8 lg:p-10 backdrop-blur-md shadow-2xl text-center">
            <div className="mb-5 sm:mb-6">
              <Link to="/" className="text-indigo-300 hover:text-white text-base sm:text-lg tracking-wide transition-colors">
                Andlegur
              </Link>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white">
              Feeling stuck,
              <br />
              lost, or unsure?
            </h1>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-left">
              {thoughts.map((thought, idx) => {
                const isSelected = selectedThoughts.includes(thought);
                return (
                  <button
                    key={thought}
                    onClick={() => toggleThought(thought)}
                    className={`relative rounded-xl border px-3.5 py-2.5 pr-10 text-sm sm:text-base leading-snug backdrop-blur-sm text-left transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-300/70 bg-[#1A4BA0]/55 text-white shadow-lg shadow-blue-900/35'
                        : idx % 2 === 0
                          ? 'border-violet-300/35 bg-[#29194A]/70 text-violet-100 hover:border-blue-300/60'
                          : 'border-indigo-300/30 bg-[#11214A]/70 text-indigo-100 hover:border-blue-300/60'
                    }`}
                    aria-pressed={isSelected}
                  >
                    <span
                      className={`absolute top-2 right-2 h-5 w-5 rounded border flex items-center justify-center text-[11px] font-bold ${
                        isSelected
                          ? 'bg-blue-500 border-blue-300 text-white'
                          : 'bg-white/90 border-blue-300/70 text-blue-500'
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? '✓' : ''}
                    </span>
                    {thought}
                  </button>
                );
              })}
            </div>

            <p className="mt-3 text-blue-300 text-sm sm:text-base font-semibold">
              Select up to 3 thoughts ({selectedThoughts.length}/3 selected).
            </p>

            <p className="mt-6 text-indigo-100 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
              You do not need all the answers. You need a place to understand what is going on.
            </p>

            <p className="mt-3 text-indigo-200 text-sm sm:text-base max-w-3xl mx-auto">
              Andlegur helps you reflect, gain insight, and find direction - privately.
            </p>

            <button
              onClick={() => { trackCTAClick('start_finding_clarity', '/feeling'); navigate('/join'); }}
              className="mt-6 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-base sm:text-lg font-semibold px-7 py-3.5 rounded-2xl transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-indigo-700/35"
            >
              Start Finding Clarity {'->'}
            </button>

            <p className="mt-4 text-indigo-300 text-xs sm:text-sm">Private | Free | Takes less than a minute</p>

            <div className="md:hidden mt-6 relative overflow-hidden rounded-2xl border border-indigo-300/30">
              <img
                src="/web_images/Andlegur%20web%2028.webp"
                alt="A person in a difficult situation looking for clarity"
                className="h-36 w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-linear-to-t from-[#05091B]/90 via-[#05091B]/35 to-transparent" />
              <p className="absolute bottom-3 left-3 right-3 text-center text-white text-sm font-semibold tracking-wide">
                Find Your Directions. Find Clarity
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
